import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Seo } from "@/components/Seo";
import { PAGE_SEO, SITE_URL, ORG_NAME, DEFAULT_OG_IMAGE, breadcrumbLd } from "@/lib/seo";
import { useBrief } from "@/context/BriefContext";
import { track } from "@/lib/analytics";

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

// Cormorant italic copper ampersand — the signature flourish.
const Amp = () => <span className="exec-amp">&amp;</span>;
const Dot = () => (
  <span className="text-[color:var(--copper)] px-2.5" aria-hidden="true">&middot;</span>
);

// Exact portrait supplied by the founder.
const FOUNDER_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_copper-vine-sprint/artifacts/5pqzuul5_RAKIA_LONG_20221020-_P227125.jpg";

// Person + Organization schema. sameAs links supplied by the client.
const personLd = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Rakia Finley",
  jobTitle: "Founder",
  image: FOUNDER_PORTRAIT,
  url: SITE_URL + "/about",
  worksFor: { "@type": "Organization", name: ORG_NAME, url: SITE_URL },
  alumniOf: { "@type": "Organization", name: "Techstars" },
  award: ["Salesforce MVP Hall of Fame"],
  sameAs: ["https://www.linkedin.com/in/rakiafinley"],
};

const organizationLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: ORG_NAME,
  alternateName: "Copper and Vine",
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
  foundingDate: "2003",
  description:
    "Independent executive technology advisory and full-stack engineering firm. Senior, solution-agnostic judgment for founders, enterprises, and the public sector since 2003.",
  founder: { "@type": "Person", name: "Rakia Finley" },
  areaServed: ["United States", "United Kingdom", "Switzerland", "Ghana", "Nigeria", "Mexico"],
  sameAs: [
    "https://www.linkedin.com/company/copper8vine",
    "https://www.instagram.com/copper_vine",
    "https://www.facebook.com/copperandvineco/",
    "https://www.twitter.com/copper_vine",
    "https://www.youtube.com/@CopperVineStudio",
    "https://www.crunchbase.com/organization/copper-vine",
  ],
};

// AIRE method — the firm's single operating discipline.
const aire = [
  { letter: "A", word: "Audit", body: "what you actually run on." },
  { letter: "I", word: "Integrate", body: "only what earns its place." },
  { letter: "R", word: "Regulate", body: "the risk before it finds you." },
  {
    letter: "E",
    word: "Evolve",
    body: "on a documented cadence, so the system keeps holding up after we leave.",
  },
];

// Three commitments.
const beliefs = [
  {
    n: "01",
    t: "Mission-centered",
    b: "Technical and commercial execution aligned to your vision, your values, and your long-term sustainability, not ours.",
  },
  {
    n: "02",
    t: "Outcome-driven",
    b: "Every sprint and deliverable ties to a named outcome, in working documents and executable frameworks that live inside your organization after we leave.",
  },
  {
    n: "03",
    t: "Transparency-first",
    b: "Every system documented, every decision recorded, every deliverable transition-ready. You hold the complete operating record.",
  },
];

const recordStats = [
  "Founded 2003",
  "400+ products and platforms",
  "20+ years",
  "Salesforce MVP Hall of Fame",
  "Women- and minority-owned",
];

const badges = ["Women- and minority-owned", "Salesforce MVP Hall of Fame"];

export default function About() {
  const navigate = useNavigate();
  const { openBrief } = useBrief();

  const goScorecard = () => {
    track("door_click", { target: "/scorecard", location: "about_cta" });
    navigate("/scorecard");
  };

  return (
    <div className="exec-scope min-h-screen" data-testid="about-page">
      <Seo
        {...PAGE_SEO.about}
        type="profile"
        jsonLd={[
          personLd,
          organizationLd,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
        ]}
      />

      {/* 1 — Opening statement */}
      <section className="exec-container pt-24 md:pt-28 pb-16" data-testid="about-opening">
        <motion.div {...fade} className="max-w-[820px]">
          <p className="exec-eyebrow">About</p>
          <h1
            data-testid="about-opening-headline"
            className="font-extralight text-[clamp(34px,4.8vw,58px)] leading-[1.1] mt-6"
          >
            Senior technology judgment, since{" "}
            <span className="exec-serif italic font-medium text-[color:var(--copper)]">2003.</span>
          </h1>
          <p className="exec-serif text-[clamp(18px,2vw,23px)] leading-[1.5] text-[color:var(--lead-ink)] mt-7">
            Copper <Amp /> Vine is an independent technology advisory and full-stack engineering firm.
            For more than twenty years we have helped founders, enterprises, and the public sector
            build the systems they run on, and left every one of them more capable, documented, and
            independent than we found it.
          </p>
          <p className="exec-serif text-[clamp(18px,2vw,23px)] leading-[1.5] text-[color:var(--lead-ink)] mt-5">
            We are not a platform reseller, and we are not a staffing shop. We are the senior team you
            bring in for the decisions you cannot afford to get wrong.
          </p>
        </motion.div>
      </section>

      {/* 2 — The founder */}
      <section className="exec-container pb-20" data-testid="about-founder">
        <div className="exec-rule mb-16" />
        <div className="lg:grid lg:grid-cols-12 lg:gap-12 lg:items-start">
          <motion.div {...fade} className="lg:col-span-7">
            <p className="exec-eyebrow">The founder</p>
            <h2
              data-testid="about-founder-name"
              className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4"
            >
              Rakia Finley.
            </h2>
            <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-6">
              Rakia Finley has spent more than twenty-five years where the hard technology calls get
              made: on-premise, in the cloud, and now in AI.
            </p>
            <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
              She founded Copper <Amp /> Vine in 2003 and built one of the first orphanage-management
              platforms before the word &ldquo;platform&rdquo; was everywhere. She helped shape the
              Salesforce Nonprofit Starter Pack and was inducted into the Salesforce MVP Hall of Fame.
              She has served as a founding CTO inside a venture-backed startup, helped secure a share
              of a &pound;6.2 million Innovate UK grant, and come through Techstars in London.
            </p>
            <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
              She brings the same thing to every engagement: senior judgment, no platform to push,
              and the honesty to tell you when the answer is not the one you wanted.
            </p>
          </motion.div>

          <motion.div {...fade} className="lg:col-span-5 mt-10 lg:mt-0">
            <div className="overflow-hidden border border-[color:var(--rule)] bg-[#efe9e1] rounded-[2px]">
              <img
                data-testid="about-founder-portrait"
                src={FOUNDER_PORTRAIT}
                alt="Rakia Finley, Founder of Copper and Vine"
                loading="lazy"
                className="w-full h-auto aspect-[4/5] object-cover object-top"
              />
            </div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--grey)] mt-3 font-normal">
              Rakia Finley, Founder
            </p>
          </motion.div>
        </div>
      </section>

      {/* 3 — The firm behind the founder */}
      <section className="exec-container pb-20" data-testid="about-firm">
        <div className="exec-rule mb-16" />
        <motion.div {...fade} className="max-w-[820px]">
          <p className="exec-eyebrow">The team</p>
          <h2 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4">
            Copper <Amp /> Vine is not one person.
          </h2>
          <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-6">
            For every engagement, we assemble a senior team scoped to the work: fractional CTOs and
            CIOs, compliance architects, product strategists, and go-to-market leads. The level of
            operator who usually sits in an executive seat, brought in for exactly the problem in
            front of you.
          </p>
          <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
            Over more than twenty years and four hundred products, that model is how we have delivered
            for the Fortune 500, federal agencies, research institutions, and high-growth startups
            alike. You get the caliber of a large firm and the focus of a small one, with none of the
            platform a large firm is quietly trying to sell you.
          </p>
        </motion.div>
      </section>

      {/* 3.5 — Why Copper & Vine (the name story) */}
      <section className="exec-container pb-20" data-testid="about-name">
        <div className="exec-rule mb-16" />
        <motion.div {...fade} className="max-w-[820px]">
          <p className="exec-eyebrow">The name</p>
          <h2
            data-testid="about-name-headline"
            className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4"
          >
            Why Copper <Amp /> Vine.
          </h2>
          <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-6">
            Two things make electricity move: a conductor and a connection.
          </p>
          <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
            Copper carries the charge, and it holds what it conducts. That is the kind of system we
            build.
          </p>
          <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
            The vine is the connection. Agile and reaching, it extends past any one place, into
            communities, teams, and organizations, flexible enough to move, strong enough to endure.
            That&rsquo;s how the work integrates: not bolted on, but grown in, and built to last.
          </p>
          <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
            The durable conductor and the agile connector. We stand in the gap between a bold idea and
            the moment it reaches the ground, and we leave behind something that keeps working after
            we&rsquo;re gone.
          </p>
          <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
            The firm has run since 2003; it took the name Copper <Amp /> Vine in 2017, when the work
            it described needed a name that meant something.
          </p>
        </motion.div>
      </section>

      {/* 4 — The independence (signature statement — light, no longer a dark band) */}
      <section className="exec-container pb-20" data-testid="about-independence">
        <div className="exec-rule mb-16" />
        <motion.div {...fade} className="max-w-[920px]">
          <p className="exec-eyebrow">The independence</p>
          <h2 className="font-light text-[clamp(28px,3.8vw,46px)] leading-[1.12] mt-5">
            The independence is the{" "}
            <span className="exec-serif italic font-medium text-[color:var(--copper)]">product.</span>
          </h2>
          <p className="exec-serif text-[clamp(18px,2vw,23px)] leading-[1.5] text-[color:var(--lead-ink)] mt-7">
            Deloitte recommends AWS. Accenture recommends Microsoft. Both have a platform to sell, and
            the recommendation tends to follow the platform.
          </p>
          <p className="exec-serif text-[clamp(18px,2vw,23px)] leading-[1.5] text-[color:var(--lead-ink)] mt-5">
            We have none. No reseller margin, no partner quota, no back-end product steering the
            advice. That independence is the product. Our recommendation is the one you would make for
            yourself, if you had the time and the team to make it. It is the single reason a
            recommendation from us reads differently from one shaped by what the firm needs to move.
          </p>
        </motion.div>
      </section>

      {/* 5 — How we think (AIRE) */}
      <section className="exec-container pb-20" data-testid="about-method">
        <div className="exec-rule mb-16" />
        <motion.div {...fade} className="max-w-[820px]">
          <p className="exec-eyebrow">The method</p>
          <h2 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4">
            How we think.
          </h2>
          <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-6">
            Every engagement runs on one method. We call it AIRE.
          </p>
        </motion.div>

        <div
          data-testid="about-aire"
          className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-10 mt-12 max-w-[820px]"
        >
          {aire.map((step) => (
            <motion.div key={step.letter} {...fade} className="flex gap-5">
              <span
                aria-hidden="true"
                className="exec-serif italic text-[color:var(--copper)] text-[34px] leading-none w-8 shrink-0"
              >
                {step.letter}
              </span>
              <div>
                <h3 className="text-[13px] uppercase tracking-[0.2em] font-normal text-[color:var(--ink)]">{step.word}</h3>
                <p className="exec-serif text-[16.5px] leading-[1.5] text-[color:var(--lead-ink)] mt-2">{step.body}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-12 max-w-[820px]">
          It is a discipline, not improvisation, and it is the reason you keep the operating record,
          not just the deliverable, when the engagement ends.
        </p>
        <Link
          to="/resources/frameworks/aire-audit-integrate-regulate-evolve"
          data-testid="about-aire-framework-link"
          onClick={() => track("framework_open", { slug: "aire-audit-integrate-regulate-evolve", location: "about_method" })}
          className="exec-link mt-7"
        >
          Read the AIRE framework
          <span aria-hidden="true">&rarr;</span>
        </Link>
      </section>

      {/* 6 — What we believe */}
      <section className="exec-container pb-20" data-testid="about-beliefs">
        <div className="exec-rule mb-16" />
        <p className="exec-eyebrow">What we believe</p>
        <h2 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4">
          Three commitments.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-10 bg-[color:var(--rule)] p-px">
          {beliefs.map((c) => (
            <motion.div key={c.n} {...fade} className="bg-[color:var(--paper)] p-8">
              <p className="exec-serif italic text-[30px] text-[color:var(--copper)] leading-none">{c.n}</p>
              <div className="exec-rule my-5" />
              <h3 className="text-[12px] uppercase tracking-[0.2em] font-normal text-[color:var(--ink)]">{c.t}</h3>
              <p className="exec-serif text-[16px] leading-[1.5] text-[color:var(--lead-ink)] mt-3">{c.b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 7 — The record */}
      <section className="exec-container pb-20" data-testid="about-record">
        <div className="exec-rule mb-16" />
        <motion.div {...fade} className="max-w-4xl">
          <p className="exec-eyebrow">The record</p>
          <h2 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4">
            Twenty years, on the record.
          </h2>

          <p
            data-testid="about-record-stats"
            className="exec-serif text-[clamp(20px,2.4vw,28px)] leading-[1.3] text-[color:var(--ink)] mt-7 flex flex-wrap items-center"
          >
            {recordStats.map((stat, i) => (
              <span key={stat} className="inline-flex items-center">
                {stat}
                {i < recordStats.length - 1 && <Dot />}
              </span>
            ))}
          </p>

          <div className="flex flex-wrap gap-3 mt-7" data-testid="about-record-badges">
            {badges.map((b) => (
              <span
                key={b}
                className="text-[10.5px] uppercase tracking-[0.16em] font-normal text-[color:var(--copper-deep)] border border-[color:var(--copper)]/45 rounded-[2px] px-3.5 py-1.5"
              >
                {b}
              </span>
            ))}
          </div>

          <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-8">
            Work for Salesforce, Apple, Howard University, the American Bar Association, the White
            House, and the Department of Energy. Delivered across the United States, the United
            Kingdom, Switzerland, Ghana, Nigeria, and Mexico.
          </p>
        </motion.div>
      </section>

      {/* In their words — partnership testimonial */}
      <section className="exec-container pb-20" data-testid="about-testimonial">
        <div className="exec-rule mb-16" />
        <motion.div {...fade} className="max-w-[820px]">
          <p className="exec-eyebrow">In their words</p>
          <blockquote className="exec-serif text-[color:var(--ink)] text-[clamp(24px,3vw,36px)] leading-[1.3] mt-6 border-l-2 border-[color:var(--copper)] pl-7">
            &ldquo;Senior, independent, and unafraid of the hard call. We left the engagement more
            capable than we went in.&rdquo;
          </blockquote>
          <div className="mt-7 pl-7">
            <p className="text-[12px] uppercase tracking-[0.18em] font-normal text-[color:var(--ink)]">
              Kenneth Watson
            </p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--grey)] mt-1 font-normal">
              CEO, HL360
            </p>
          </div>
        </motion.div>
      </section>

      {/* 8 — The next step */}
      <section className="exec-container pb-24" data-testid="about-cta">
        <div className="border-t border-[color:var(--rule)] pt-16 max-w-[820px]">
          <p className="exec-eyebrow">The next step</p>
          <h2 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4">
            Start where you stand.
          </h2>
          <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.55] text-[color:var(--lead-ink)] mt-6">
            You&rsquo;ve seen how we think and who does the work. The best next move is the smallest
            one: take the Scorecard for a free read on where you stand, or request the brief, and
            we&rsquo;ll send the detail before you decide anything.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-10">
            <button
              data-testid="about-cta-scorecard-button"
              className="exec-btn exec-btn-primary"
              onClick={goScorecard}
            >
              Take the Scorecard
            </button>
            <button
              data-testid="about-cta-brief-button"
              className="exec-btn exec-btn-secondary"
              onClick={() => {
                track("door_click", { target: "brief_request", location: "about_cta" });
                openBrief("general");
              }}
            >
              Request the brief
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
